import type {
  MbscCalendarEvent,
  MbscEventcalendarView,
} from '@mobiscroll/react';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { RowSelectionState } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import {
  AssignmentSorting,
  assignmentSortStorageKey,
  autoScrollCalendarStorageKey,
  viewTripDetailsOnHoverStorageKey,
} from './constants';
import { execTripMenuAction } from './execTripMenuAction';
import type { CallRequestModalInputs } from './modals/CallRequestModal';
import { selectEffectiveDispatchResponse } from './selectEffectiveDispatchResponse';
import { useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import type {
  AssignedDriverRead,
  AssignmentBreakRead,
  AssignmentRead,
} from 'src/common/external/bambi-api/assignmentsApi';
import type {
  County,
  DispatchTripPayerOrganizationRead,
  DispatchTripRead,
  ModeEnum,
  OverrideStatusEnum,
  PayerOrganizationRead,
  RunBambiRunResultRead,
  ServiceTypeEnum,
  SpaceTypeEnum,
  TagRead,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { bambiApi } from 'src/common/external/bambi-api/bambiApi';
import type {
  BulkTripValidateResults,
  PatchedTripValidate,
  PatchedTripValidateRead,
  TripsBulkValidateCreateApiResponse,
} from 'src/common/external/bambi-api/tripsApi';
import { bambiApi as tripsApi } from 'src/common/external/bambi-api/tripsApi';
import type { NumberRangeValue } from 'src/common/primitives/NumberRangeFilterPopover/NumberRangeFilterPopover';
import serverErrorFormatter from 'src/common/util/serverErrorFormatter';
import { getStorageValue } from 'src/common/util/useLocalStorage/getStorageValue';
import { setStorageValue } from 'src/common/util/useLocalStorage/setStorageValue';

export type DispatchModalKey =
  | 'assignmentBreak'
  | 'callRequest'
  | 'cancelTrip'
  | 'cancelSuggestions'
  | 'confirmTripActivation'
  | 'deleteBreak'
  | 'itineraryLateness'
  | 'unassign'
  | 'acceptRequestConfirm'
  | 'rejectRequestConfirm'
  | 'bulkRejectRequestConfirm'
  | 'rbrAssignmentSelection';

export type RequestedTripIdsToReasonCodesMap = {
  [k: string]: string | null;
};

export type UnassignedTripsSorting =
  | 'scheduled_pickup_time'
  | 'passenger__full_name';
export type MultiloadFlowMode =
  | 'assign-trip'
  | 'edit-trip-multi-load'
  | 'create-assigned-trip-multi-load'
  | 'add-to-existing-multi-load'
  | 'assign-to-existing-multi-load'
  | 'bulk-assign-trips';

export type UnassignedTripCategoryEnum =
  | 'scheduled-trips'
  | 'will-call-trips'
  | 'requested-trips';
export interface IDispatchState {
  selectedDate: string;
  selectedTripId: string | number | undefined;
  selectedUnassignedTripIds: string[];
  // Trips whose menu action is in flight; they render dimmed (calendar +
  // unassigned list) until fresh board data arrives.
  pendingTripIds: string[];
  // The latest execTripMenuAction request; completions of superseded
  // requests must not clear the newer action's in-flight state.
  activeMenuActionRequestId: string | null;
  isMenuActionInProgress: boolean;
  isRunBambiRunInProgress: boolean;
  isRunBambiRunApplyInProgress: boolean;
  runBambiRunJobId?: string;
  runBambiRunMode: ModeEnum;
  runBambiRunResult?: RunBambiRunResultRead;
  unassignedTripsSearchTerm: string;
  unassignedTripsFilters: {
    price: NumberRangeValue | null;
    startTime: string;
    endTime: string;
    payers: PayerOrganizationRead[];
    spaceTypes: SpaceTypeEnum[];
    serviceTypes: ServiceTypeEnum[];
    zipCodes: string[];
    counties: County[];
    tags: TagRead[];
    passengerTags: TagRead[];
    categories: UnassignedTripCategoryEnum[];
  };
  dispatchCalendarFilters: {
    drivers: AssignedDriverRead[];
    vehicleTags: TagRead[];
    driverTags: TagRead[];
    statuses: OverrideStatusEnum[];
    payers: DispatchTripPayerOrganizationRead[];
    spaceTypes: SpaceTypeEnum[];
    tripTags: TagRead[];
    passengerTags: TagRead[];
  };
  openTripDropdownMenuId: string | null;
  runBambiRunError?: string | null;
  runBambiRunShouldMultiload: boolean;
  tripAssignmentsToApplyIds: { [k: string]: boolean };
  tripAssignmentFlow: {
    isAssignTripModalOpen: boolean;
    tripId: string;
    assignmentId: string;
    validateAssignmentResponse?: PatchedTripValidateRead | null;
    status?: OverrideStatusEnum;
  };
  willCallScheduleOverrideFlow: {
    isOpen: boolean;
    tripId: string;
    assignmentId: string;
    status?: OverrideStatusEnum;
    originalPickupAt: string;
    suggestedPickupAt: string;
    crewStart: string;
    crewEnd: string;
  };
  bulkTripAssignmentFlow: {
    isAssignTripModalOpen: boolean;
    tripIds: string[];
    assignmentId: string;
    validateBulkAssignmentsResponse?: TripsBulkValidateCreateApiResponse | null;
  };
  multiloadAssignmentFlow: {
    // We're not necessarily creating a multi-load, so being explicit with the action
    // being taken that opened the flow rather than multi-load oriented modes like create/edit
    mode: MultiloadFlowMode;
    isMultiloadAssignmentFlowOpen: boolean;
    originatingTripId: string;
    selectedTripIdsToMultiload: string[];
    assignmentIdToMultiload: string;
    potentialMultiloadTrips: string[];
    existingMultiloadTripIds: string[];
    // Full bulk selection when mode === 'bulk-assign-trips'; trips not multiloaded ride along through the normal bulk assign sequence on completion.
    bulkSelectedTripIds: string[];
    multiLoadId?: string;
  };
  multiloadReassignFlow: {
    isOpen: boolean;
    tripIds: string[];
    assignmentId: string;
    // null until a result needs showing:
    status:
      | 'idle'
      | 'running'
      | 'validation_error'
      | 'no_merge_blocked'
      | 'server_error';
    validationResults: BulkTripValidateResults | null;
    serverErrorMessage: string | null;
  };
  modals: {
    modalToShow: DispatchModalKey | null;
    requestedTripId?: string | null;
    requestActionConfirmModalRowSelection: RowSelectionState;
    // This is a map of tripId to reason code
    // There's no enum for codes and they're per trip, so we just enforce a string
    // The api will error if the code is invalid. Codes are fetched and displayed
    // to user in a dropdown
    requestedTripIdsToRejectToReasonCodes: RequestedTripIdsToReasonCodesMap;
    // When set, the reject-request confirm is the "accept this leg, reject the
    // other" step of a wait-and-return prompt: this leg is accepted only once
    // the sibling's rejection is confirmed, so cancelling aborts the whole
    // action (the leg is never left accepted with the pairing still intact).
    acceptLegIdAfterReject?: string | null;
    assignmentId: string | null;
    assignmentBreak: AssignmentBreakRead | null;
    callRequestInputs: CallRequestModalInputs | null;
    confirmTripActivationTripId: string | null;
    selectedTrip: TripRead | null;
  };
  previewEvents?: MbscCalendarEvent[];
  // A drag-initiated trip action (validate → confirm/override modals →
  // mutation) still in flight. While set, TripActionPreview keeps a
  // destination preview on the board — which also pauses dispatch polling —
  // so the dragged trip doesn't visually snap back to its source mid-flow.
  // Cleared by terminal transitions: fresh dispatch data, cancel, or error.
  pendingTripAction: {
    tripIds: string[];
    assignmentId: string;
  } | null;
  isDispatchSidebarOpen: boolean;
  showToggleCalendarScrollButton: boolean;
  isEventCalendarScrollable: boolean;
  selectedAssignments: string[];
  assignmentColumnVisualState: {
    [k: string]:
      | {
          isExpanded: boolean;
          isWidthSmall: boolean;
        }
      | undefined;
  };
  assignmentEdit: {
    assignmentId: string | null;
  };
  calendarEventDragState: {
    isDragging: boolean;
  };
  autoScrollCalendar: boolean;
  viewTripDetailsOnHover: boolean;
  calendarView: MbscEventcalendarView;
  selectedAssignedTripSearchId: string | null;
  unassignedTripsSorting: UnassignedTripsSorting;
  unassignedTripsSortingDirection: 'asc' | 'desc';
  assignmentSorting: AssignmentSorting;
  // Multiloads the dispatcher opened up into individual trip events
  // (multiload calendar events flag)
  expandedMultiloadIds: string[];
}

// All values (in minutes) according to Mobiscroll's documentation
// 1, 5, 10, 15, 20, 30, 60, 120, 180, 240, 360, 480, 720, 1440
// I limited these to what looked good  at the time so we can expand as people complain
export type MobiscrollCellStepValues =
  | 1
  | 5
  | 10
  | 15
  | 20
  | 30
  | 60
  | 120
  | 180
  | 240
  | 360
  | 480
  | 720
  | 1440;
export const possibleZoomLevelsInMinutes: MobiscrollCellStepValues[] = [
  10, 15, 20, 30, 60, 120,
];
export const possibleLabelStepValuesForZoomIndex: MobiscrollCellStepValues[] = [
  15, 15, 20, 30, 60, 120,
];

export const initialState: IDispatchState = {
  selectedDate: '',
  selectedTripId: undefined,
  selectedUnassignedTripIds: [],
  pendingTripIds: [],
  activeMenuActionRequestId: null,
  isMenuActionInProgress: false,
  isRunBambiRunInProgress: false,
  isRunBambiRunApplyInProgress: false,
  runBambiRunJobId: undefined,
  runBambiRunMode: 'optimal',
  runBambiRunShouldMultiload: false,
  unassignedTripsSearchTerm: '',
  unassignedTripsFilters: {
    price: null,
    startTime: '',
    endTime: '',
    payers: [],
    spaceTypes: [],
    serviceTypes: [],
    zipCodes: [],
    counties: [],
    tags: [],
    passengerTags: [],
    categories: [],
  },
  dispatchCalendarFilters: {
    drivers: [],
    vehicleTags: [],
    driverTags: [],
    statuses: [],
    payers: [],
    spaceTypes: [],
    tripTags: [],
    passengerTags: [],
  },
  openTripDropdownMenuId: null,
  tripAssignmentsToApplyIds: {},
  tripAssignmentFlow: {
    isAssignTripModalOpen: false,
    tripId: '',
    assignmentId: '',
    validateAssignmentResponse: null,
  },
  willCallScheduleOverrideFlow: {
    isOpen: false,
    tripId: '',
    assignmentId: '',
    originalPickupAt: '',
    suggestedPickupAt: '',
    crewStart: '',
    crewEnd: '',
  },
  bulkTripAssignmentFlow: {
    isAssignTripModalOpen: false,
    tripIds: [],
    assignmentId: '',
    validateBulkAssignmentsResponse: null,
  },
  multiloadAssignmentFlow: {
    mode: 'assign-trip',
    isMultiloadAssignmentFlowOpen: false,
    originatingTripId: '',
    selectedTripIdsToMultiload: [],
    assignmentIdToMultiload: '',
    potentialMultiloadTrips: [],
    existingMultiloadTripIds: [],
    bulkSelectedTripIds: [],
  },
  multiloadReassignFlow: {
    isOpen: false,
    tripIds: [],
    assignmentId: '',
    status: 'idle',
    validationResults: null,
    serverErrorMessage: null,
  },
  modals: {
    modalToShow: null,
    assignmentId: null,
    assignmentBreak: null,
    callRequestInputs: null,
    confirmTripActivationTripId: null,
    selectedTrip: null,
    requestedTripIdsToRejectToReasonCodes: {},
    requestActionConfirmModalRowSelection: {},
    acceptLegIdAfterReject: null,
  },
  pendingTripAction: null,
  isDispatchSidebarOpen: true,
  showToggleCalendarScrollButton: true,
  isEventCalendarScrollable: false,
  selectedAssignments: [],
  assignmentColumnVisualState: {},
  assignmentEdit: {
    assignmentId: null,
  },
  calendarEventDragState: {
    isDragging: false,
  },
  autoScrollCalendar: false,
  // Hydrated from localStorage at init so hover applies on page load, without
  // waiting for the settings menu (where the toggle lives) to be opened.
  viewTripDetailsOnHover:
    getStorageValue<string>(viewTripDetailsOnHoverStorageKey, 'false') ===
    'true',
  assignmentSorting: AssignmentSorting.START_TIME,
  expandedMultiloadIds: [],
  calendarView: {
    schedule: {
      type: 'day',
      allDay: false,
      timeCellStep: 30,
      timeLabelStep: 60,
    },
  },
  selectedAssignedTripSearchId: null,
  unassignedTripsSorting: 'scheduled_pickup_time',
  unassignedTripsSortingDirection: 'asc',
};

const stateKeysToResetOnDateChange = [
  'selectedTripId',
  'selectedUnassignedTripIds',
  'assignmentColumnVisualState',
];

// These are values we intend to start fresh on a date change
// For ex: If selectedUnassignedTripIds has values and then we change date then run RBR
// RBR gets a trip outside of the date range and blows up
function resetOnDateChange(state: IDispatchState) {
  stateKeysToResetOnDateChange.forEach((key) => {
    // TODO: make ts happy
    // @ts-ignore
    state[key] = initialState[key];
  });
}

export const dispatcherSlice = createSlice({
  name: 'dispatcher',
  initialState,
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
      resetOnDateChange(state);
    },
    setSelectedDateToToday: (state) => {
      state.selectedDate = DateTime.local().toISODate();
      resetOnDateChange(state);
    },
    setSelectedDateToNextDay: (state) => {
      state.selectedDate = DateTime.fromISO(state.selectedDate)
        .plus({ days: 1 })
        .toISODate();
      resetOnDateChange(state);
    },
    setSelectedDateToPreviousDay: (state) => {
      state.selectedDate = DateTime.fromISO(state.selectedDate)
        .minus({ days: 1 })
        .toISODate();
      resetOnDateChange(state);
    },
    setSelectedUnassignedTripIds: (state, action) => {
      state.selectedUnassignedTripIds = action.payload;
    },
    // For mutation paths that don't go through execTripMenuAction (e.g. the
    // bulk-assign popover); exec-driven actions set this via meta.arg.tripIds.
    setPendingTripIds: (state, action: PayloadAction<string[]>) => {
      state.pendingTripIds = action.payload;
    },
    setUnassignedTripsSearchTerm(state, action) {
      state.unassignedTripsSearchTerm = action.payload;
    },
    setUnassignedTripsFilters: (
      state,
      action: PayloadAction<IDispatchState['unassignedTripsFilters']>
    ) => {
      state.unassignedTripsFilters = action.payload;
    },
    setOpenTripDropdownMenuId: (state, action) => {
      state.openTripDropdownMenuId = action.payload;
    },
    runBambiRunRouteInit: (state) => {
      state.isRunBambiRunInProgress = true;
      state.runBambiRunJobId = undefined;
      state.runBambiRunError = null;
      state.isRunBambiRunApplyInProgress = false;
    },
    setRunBambiRunMode: (state, action: PayloadAction<ModeEnum>) => {
      state.runBambiRunMode = action.payload;
    },
    dispatchRouteInit: (state) => {
      state.isRunBambiRunInProgress = false;
      state.runBambiRunJobId = undefined;
      state.runBambiRunError = null;
      state.selectedDate = state.selectedDate || DateTime.local().toISODate();
    },

    onSelectAllNewAssignments: (
      state,
      action: { payload: { selectedState: boolean } }
    ) => {
      Object.keys(state.tripAssignmentsToApplyIds).forEach((tripId) => {
        state.tripAssignmentsToApplyIds[tripId] = action.payload.selectedState;
      });
    },
    toggleRunBambiRunTripAssignmentSelection: (
      state,
      action: { payload: { tripId: string; selectedState: boolean } }
    ) => {
      state.tripAssignmentsToApplyIds[action.payload.tripId] =
        action.payload.selectedState;
    },
    removeRunBambiRunSuggestion: (
      state,
      action: { payload: { tripId: string } }
    ) => {
      state.tripAssignmentsToApplyIds[action.payload.tripId] = false;
    },
    onApplyRunBambiRunWithRequestedTrips: (state) => {
      state.modals.modalToShow = 'acceptRequestConfirm';
    },
    startRunBambiRunApply: (state) => {
      state.isRunBambiRunApplyInProgress = true;
    },
    endRunBambiRunApply: (state) => {
      state.isRunBambiRunApplyInProgress = false;

      // If we're successful, we navigate away so let's clean up our state
      if (!state.runBambiRunError) {
        state.runBambiRunResult = initialState.runBambiRunResult;
      }
    },
    onRunBambiRunShouldMultiloadChange: (
      state,
      action: { payload: boolean }
    ) => {
      state.runBambiRunShouldMultiload = action.payload;
    },
    onPreviewTripAssignmentStart: (
      state,
      action: { payload: MbscCalendarEvent[]; type: string }
    ) => {
      state.previewEvents = action.payload;
    },
    onPreviewTripAssignmentEnd: (state) => {
      state.previewEvents = undefined;
    },
    onTripActionStart: (
      state,
      action: PayloadAction<{ tripIds: string[]; assignmentId: string }>
    ) => {
      state.pendingTripAction = action.payload;
    },
    onTripActionEnd: (state) => {
      state.pendingTripAction = null;
    },
    onTripAssignmentInvalid: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
          validateAssignmentResponse: PatchedTripValidate;
          status: OverrideStatusEnum;
        };
      }
    ) => {
      state.tripAssignmentFlow = {
        isAssignTripModalOpen: true,
        ...action.payload,
      };
    },
    onBulkTripAssignmentInvalid: (
      state,
      action: {
        payload: {
          tripIds: string[];
          assignmentId: string;
          validateBulkAssignmentsResponse: TripsBulkValidateCreateApiResponse;
        };
      }
    ) => {
      state.bulkTripAssignmentFlow = {
        isAssignTripModalOpen: true,
        ...action.payload,
      };
    },
    // Flow-state reset only. The optimistic board revert (removing the trip
    // from assigned_trips) lives in the cancelAssignTrip thunk, which patches
    // the RTK cache before dispatching this.
    onCancelAssignTrip: (state) => {
      state.pendingTripAction = null;
      state.tripAssignmentFlow = {
        ...initialState.tripAssignmentFlow,
        isAssignTripModalOpen: false,
      };
      state.selectedTripId = undefined;
    },
    onCancelBulkAssignTrip: (state) => {
      state.bulkTripAssignmentFlow = {
        ...initialState.bulkTripAssignmentFlow,
        isAssignTripModalOpen: false,
      };
      state.selectedUnassignedTripIds = [];
    },
    onConfirmAssignTrip: (state) => {
      state.tripAssignmentFlow = {
        ...initialState.tripAssignmentFlow,
        isAssignTripModalOpen: false,
      };
    },
    onWillCallScheduleOverride: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
          status: OverrideStatusEnum;
          originalPickupAt: string;
          suggestedPickupAt: string;
          crewStart: string;
          crewEnd: string;
        };
      }
    ) => {
      state.willCallScheduleOverrideFlow = {
        isOpen: true,
        ...action.payload,
      };
    },
    onCancelWillCallScheduleOverride: (state) => {
      state.pendingTripAction = null;
      state.willCallScheduleOverrideFlow = {
        ...initialState.willCallScheduleOverrideFlow,
      };
    },
    onConfirmWillCallScheduleOverride: (state) => {
      state.willCallScheduleOverrideFlow = {
        ...initialState.willCallScheduleOverrideFlow,
      };
    },
    onConfirmBulkAssignTrip: (state) => {
      state.bulkTripAssignmentFlow = {
        ...initialState.bulkTripAssignmentFlow,
        isAssignTripModalOpen: false,
      };
    },
    resetSelectedTrip: (state) => {
      state.selectedTripId = undefined;
    },
    setModalAssignmentId: (state, action: PayloadAction<string>) => {
      state.modals.assignmentId = action.payload;
    },
    setModalAssignmentBreak: (
      state,
      action: PayloadAction<AssignmentBreakRead>
    ) => {
      state.modals.assignmentBreak = action.payload;
    },
    setModalCallRequestInputs: (
      state,
      action: PayloadAction<CallRequestModalInputs>
    ) => {
      state.modals.callRequestInputs = action.payload;
    },
    setModalConfirmTripActivationTripId: (
      state,
      action: PayloadAction<string>
    ) => {
      state.modals.confirmTripActivationTripId = action.payload;
    },
    setModalSelectedTrip: (state, action: PayloadAction<TripRead | null>) => {
      state.modals.selectedTrip = action.payload;
    },
    showDispatchModal: (state, action: PayloadAction<DispatchModalKey>) => {
      state.modals.modalToShow = action.payload;
    },
    dismissDispatchModal: (state) => {
      state.modals = initialState.modals;
    },
    toggleDispatchSidebar: (state) => {
      state.isDispatchSidebarOpen = !state.isDispatchSidebarOpen;
    },
    setEventCalendarScrollable: (state, action) => {
      state.isEventCalendarScrollable = action.payload;
    },
    setShowToggleCalendarScrollButton: (state, action) => {
      state.showToggleCalendarScrollButton = action.payload;
    },
    onRequestedTripAssignment: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
        };
      }
    ) => {
      state.modals.modalToShow = 'acceptRequestConfirm';
      state.modals.requestedTripId = action.payload.tripId;
      state.modals.assignmentId = action.payload.assignmentId;
    },
    onRejectUnassignedRequestedTrips: (
      state,
      action: {
        payload: {
          tripIdsToReject: string[];
        };
      }
    ) => {
      const tripIdsToReject = action.payload.tripIdsToReject;
      state.modals.modalToShow = 'rejectRequestConfirm';
      state.modals.requestActionConfirmModalRowSelection =
        tripIdsToReject.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {} as RowSelectionState);
      state.modals.requestedTripIdsToRejectToReasonCodes =
        tripIdsToReject.reduce((acc, curr) => {
          acc[curr] = null;
          return acc;
        }, {} as RequestedTripIdsToReasonCodesMap);
    },
    // TODO: Shame - These are also used for the all requests view
    // Noodling on a few better places for accept/reject:
    // 1. The trips module - Put all the various trip actions in one place
    // 2. The rom module - only add accept/reject here? But we're also rejecting rop and kinetik trips
    // 3. Some other module?
    // For now, just leaving here
    onSingleTripRequestRejection: (
      state,
      action: {
        payload: {
          tripId: string;
        };
      }
    ) => {
      state.modals.modalToShow = 'rejectRequestConfirm';
      state.modals.requestedTripIdsToRejectToReasonCodes[
        action.payload.tripId
      ] = null;
    },
    // Wait-and-return "accept this leg, reject the other": open the single
    // reject-reason flow for the sibling and defer accepting this leg until that
    // rejection is confirmed (see acceptLegIdAfterReject). Cancelling the reject
    // aborts everything, so the leg is never left accepted with the W&R pairing
    // still intact (which would keep its wait time on assignment).
    onAcceptLegRejectSibling: (
      state,
      action: {
        payload: {
          acceptLegId: string;
          siblingId: string;
        };
      }
    ) => {
      state.modals.modalToShow = 'rejectRequestConfirm';
      state.modals.requestedTripIdsToRejectToReasonCodes = {
        [action.payload.siblingId]: null,
      };
      state.modals.acceptLegIdAfterReject = action.payload.acceptLegId;
    },
    onAcceptUnassignedRequestedTrips: (
      state,
      action: {
        payload: {
          tripIdsToAccept: string[];
        };
      }
    ) => {
      const tripIdsToAccept = action.payload.tripIdsToAccept;
      state.modals.modalToShow = 'acceptRequestConfirm';
      state.modals.requestActionConfirmModalRowSelection =
        tripIdsToAccept.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {} as RowSelectionState);
    },
    onSingleTripRequestAcceptance: (
      state,
      action: {
        payload: {
          tripId: string;
        };
      }
    ) => {
      state.modals.modalToShow = 'acceptRequestConfirm';
      state.modals.requestedTripId = action.payload.tripId;
    },
    onTripMenuCancelRequest: (
      state,
      action: { payload: { trip: TripRead | null } }
    ) => {
      state.modals.modalToShow = 'cancelSuggestions';
      state.modals.selectedTrip = action.payload.trip;
    },
    onRejectReasonChange: (
      state,
      action: { payload: { tripId: string; reason: string | null } }
    ) => {
      state.modals.requestedTripIdsToRejectToReasonCodes[
        action.payload.tripId
      ] = action.payload.reason;
    },
    setRequestedTripRejectReasons: (
      state,
      action: { payload: RequestedTripIdsToReasonCodesMap }
    ) => {
      state.modals.requestedTripIdsToRejectToReasonCodes = action.payload;
    },
    setRequestActionConfirmModalRowSelection: (
      state,
      action: { payload: RowSelectionState }
    ) => {
      state.modals.requestActionConfirmModalRowSelection = action.payload;
    },
    onCancelRunBambiRun: (state) => {
      state.runBambiRunResult = undefined;
    },
    onBulkAssignRequestedValidTrips: (
      state,
      action: { payload: { assignmentId: string } }
    ) => {
      state.modals.modalToShow = 'acceptRequestConfirm';
      state.modals.assignmentId = action.payload.assignmentId;
    },
    onApplySelectedAssignments: (state, action: { payload: string[] }) => {
      state.selectedAssignments = action.payload;
    },
    onRunBambiRunButtonClick: (state) => {
      state.modals.modalToShow = 'rbrAssignmentSelection';
    },
    onRunBambiRunCancel: (state) => {
      state.modals = initialState.modals;
      state.runBambiRunResult = initialState.runBambiRunResult;
      state.runBambiRunError = initialState.runBambiRunError;
    },
    onMultiloadAssignmentCheckedChange: (
      state,
      action: { payload: { tripId: string; checked: boolean } }
    ) => {
      const { tripId, checked } = action.payload;
      if (
        !checked &&
        state.multiloadAssignmentFlow.existingMultiloadTripIds.includes(tripId)
      ) {
        return;
      }
      if (checked) {
        state.multiloadAssignmentFlow.selectedTripIdsToMultiload.push(tripId);
      } else {
        state.multiloadAssignmentFlow.selectedTripIdsToMultiload =
          state.multiloadAssignmentFlow.selectedTripIdsToMultiload.filter(
            (id) => id !== tripId
          );
      }
    },
    onMultiloadAssignmentStart: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
          potentialMultiloadTrips: string[];
        };
      }
    ) => {
      state.multiloadAssignmentFlow.mode = 'assign-trip';
      state.multiloadAssignmentFlow.originatingTripId = action.payload.tripId;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.assignmentId;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;
      state.multiloadAssignmentFlow.potentialMultiloadTrips =
        action.payload.potentialMultiloadTrips;
      state.multiloadAssignmentFlow.existingMultiloadTripIds = [];
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
        action.payload.tripId,
      ];
    },
    onBulkMultiloadAssignmentStart: (
      state,
      action: {
        payload: {
          anchorTripId: string;
          assignmentId: string;
          potentialMultiloadTrips: string[];
          preselectedTripIds: string[];
          bulkSelectedTripIds: string[];
        };
      }
    ) => {
      state.multiloadAssignmentFlow.mode = 'bulk-assign-trips';
      state.multiloadAssignmentFlow.originatingTripId =
        action.payload.anchorTripId;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.assignmentId;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;
      state.multiloadAssignmentFlow.potentialMultiloadTrips =
        action.payload.potentialMultiloadTrips;
      state.multiloadAssignmentFlow.existingMultiloadTripIds = [];
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload =
        action.payload.preselectedTripIds;
      state.multiloadAssignmentFlow.bulkSelectedTripIds =
        action.payload.bulkSelectedTripIds;
    },
    onMultiloadFlowCancel: (state) => {
      state.multiloadAssignmentFlow = {
        ...initialState.multiloadAssignmentFlow,
      };
    },
    onMultiloadFlowSuccess: (state) => {
      state.multiloadAssignmentFlow = {
        ...initialState.multiloadAssignmentFlow,
      };
    },
    onMultiloadReassignStart: (
      state,
      action: PayloadAction<{ tripIds: string[]; assignmentId: string }>
    ) => {
      state.multiloadReassignFlow.isOpen = true;
      state.multiloadReassignFlow.tripIds = action.payload.tripIds;
      state.multiloadReassignFlow.assignmentId = action.payload.assignmentId;
      state.multiloadReassignFlow.status = 'running';
      state.multiloadReassignFlow.validationResults = null;
      state.multiloadReassignFlow.serverErrorMessage = null;
    },
    onMultiloadReassignValidationError: (
      state,
      action: PayloadAction<{ results: BulkTripValidateResults }>
    ) => {
      state.multiloadReassignFlow.status = 'validation_error';
      state.multiloadReassignFlow.validationResults = action.payload.results;
      // Drop any stale server error from a prior failed attempt.
      state.multiloadReassignFlow.serverErrorMessage = null;
    },
    onMultiloadReassignNoMerge: (state) => {
      state.multiloadReassignFlow.status = 'no_merge_blocked';
    },
    onMultiloadReassignServerError: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.multiloadReassignFlow.status = 'server_error';
      state.multiloadReassignFlow.serverErrorMessage = action.payload.message;
      // Drop any stale validation results from a prior failed attempt.
      state.multiloadReassignFlow.validationResults = null;
    },
    onMultiloadReassignClose: (state) => {
      state.multiloadReassignFlow = { ...initialState.multiloadReassignFlow };
    },
    onMultiloadSelectAll: (state, action: { payload: boolean }) => {
      const {
        mode,
        originatingTripId,
        potentialMultiloadTrips,
        existingMultiloadTripIds,
      } = state.multiloadAssignmentFlow;
      const isEditMode = mode === 'edit-trip-multi-load';
      if (action.payload) {
        state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
          ...new Set([
            ...(isEditMode ? [] : [originatingTripId]),
            ...potentialMultiloadTrips,
          ]),
        ];
      } else {
        state.multiloadAssignmentFlow.selectedTripIdsToMultiload = isEditMode
          ? []
          : [...new Set([originatingTripId, ...existingMultiloadTripIds])];
      }
    },
    onMultiloadAssignmentEdit: (
      state,
      action: {
        payload: {
          trip: DispatchTripRead;
          assignment: AssignmentRead;
          currentMultiLoadTripIds: string[];
        };
      }
    ) => {
      if (typeof action.payload.trip.multiload_id !== 'string') return;
      state.multiloadAssignmentFlow.mode = 'edit-trip-multi-load';
      state.multiloadAssignmentFlow.originatingTripId = action.payload.trip.id;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.assignment.id;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;
      state.multiloadAssignmentFlow.multiLoadId =
        action.payload.trip.multiload_id;

      const { currentMultiLoadTripIds } = action.payload;
      // Only show trips that are part of the multiload and pre-select them
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
        ...currentMultiLoadTripIds,
      ];
      state.multiloadAssignmentFlow.potentialMultiloadTrips = [
        ...currentMultiLoadTripIds,
      ];
      state.multiloadAssignmentFlow.existingMultiloadTripIds = [];
    },
    onCreateAssignedTripMultiLoad: (
      state,
      action: {
        payload: {
          trip: DispatchTripRead;
          potentialMultiloadTrips: string[];
        };
      }
    ) => {
      // Only assigned trips can seed a multiload on their crew.
      if (!action.payload.trip.assignment_id) return;
      state.multiloadAssignmentFlow.mode = 'create-assigned-trip-multi-load';
      state.multiloadAssignmentFlow.originatingTripId = action.payload.trip.id;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.trip.assignment_id;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;

      state.multiloadAssignmentFlow.potentialMultiloadTrips =
        action.payload.potentialMultiloadTrips;
      state.multiloadAssignmentFlow.existingMultiloadTripIds = [];
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
        action.payload.trip.id,
      ];
    },
    onAddToExistingMultiLoad: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
          existingMultiloadTripIds: string[];
          potentialMultiloadTrips?: string[];
        };
      }
    ) => {
      const { existingMultiloadTripIds, potentialMultiloadTrips } =
        action.payload;
      state.multiloadAssignmentFlow.mode = 'add-to-existing-multi-load';
      state.multiloadAssignmentFlow.originatingTripId = action.payload.tripId;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.assignmentId;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;
      state.multiloadAssignmentFlow.existingMultiloadTripIds =
        existingMultiloadTripIds;
      // Existing multiload members must always appear even if outside the time window
      state.multiloadAssignmentFlow.potentialMultiloadTrips = [
        ...new Set([
          ...existingMultiloadTripIds,
          ...(potentialMultiloadTrips ?? []),
        ]),
      ];
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
        action.payload.tripId,
        ...existingMultiloadTripIds,
      ];
    },
    onAssignToExistingMultiLoad: (
      state,
      action: {
        payload: {
          tripId: string;
          assignmentId: string;
          existingMultiloadTripIds: string[];
          potentialMultiloadTrips?: string[];
        };
      }
    ) => {
      const { existingMultiloadTripIds, potentialMultiloadTrips } =
        action.payload;
      state.multiloadAssignmentFlow.mode = 'assign-to-existing-multi-load';
      state.multiloadAssignmentFlow.originatingTripId = action.payload.tripId;
      state.multiloadAssignmentFlow.assignmentIdToMultiload =
        action.payload.assignmentId;
      state.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen = true;
      state.multiloadAssignmentFlow.existingMultiloadTripIds =
        existingMultiloadTripIds;
      // Existing multiload members must always appear even if outside the time window
      state.multiloadAssignmentFlow.potentialMultiloadTrips = [
        ...new Set([
          ...existingMultiloadTripIds,
          ...(potentialMultiloadTrips ?? []),
        ]),
      ];
      state.multiloadAssignmentFlow.selectedTripIdsToMultiload = [
        action.payload.tripId,
        ...existingMultiloadTripIds,
      ];
    },
    toggleMultiloadExpanded: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.expandedMultiloadIds.indexOf(id);
      if (index === -1) {
        state.expandedMultiloadIds.push(id);
      } else {
        state.expandedMultiloadIds.splice(index, 1);
      }
    },
    toggleAssignmentColumnExpanded: (state, action: PayloadAction<string>) => {
      let existingState = state.assignmentColumnVisualState[action.payload];
      if (!existingState) {
        existingState = {
          isExpanded: false,
          isWidthSmall: false,
        };
      }
      state.assignmentColumnVisualState[action.payload] = {
        ...existingState,
        isExpanded: !existingState.isExpanded,
      };
    },
    setAssignmentColumnIsWidthSmall: (
      state,
      action: PayloadAction<{
        assignmentId: string;
        isWidthSmall: boolean;
      }>
    ) => {
      let existingState =
        state.assignmentColumnVisualState[action.payload.assignmentId];
      if (!existingState) {
        existingState = {
          isExpanded: false,
          isWidthSmall: false,
        };
      }
      state.assignmentColumnVisualState[action.payload.assignmentId] = {
        ...existingState,
        isWidthSmall: action.payload.isWidthSmall,
      };
    },
    onAssignmentEditStart: (
      state,
      action: PayloadAction<{ assignmentId: string }>
    ) => {
      state.assignmentEdit.assignmentId = action.payload.assignmentId;
    },
    onAssignmentEditCancel: (state) => {
      state.assignmentEdit = initialState.assignmentEdit;
    },
    onCalendarEventDragStart: (state) => {
      state.calendarEventDragState.isDragging = true;
    },
    onCalendarEventDragEnd: (state) => {
      state.calendarEventDragState.isDragging = false;
    },
    onDispatchCalendarFiltersApply: (
      state,
      action: PayloadAction<typeof initialState.dispatchCalendarFilters>
    ) => {
      state.dispatchCalendarFilters = action.payload;
    },
    onDispatchCalendarFiltersClear: (state) => {
      state.dispatchCalendarFilters = {
        ...initialState.dispatchCalendarFilters,
      };
    },
    setAutoScrollCalendar: (state, action: PayloadAction<boolean>) => {
      setStorageValue(autoScrollCalendarStorageKey, action.payload);
      state.autoScrollCalendar = action.payload;
    },
    setViewTripDetailsOnHover: (state, action: PayloadAction<boolean>) => {
      setStorageValue(viewTripDetailsOnHoverStorageKey, action.payload);
      state.viewTripDetailsOnHover = action.payload;
    },
    setAssignmentSorting: (state, action: PayloadAction<AssignmentSorting>) => {
      setStorageValue(assignmentSortStorageKey, action.payload);
      state.assignmentSorting = action.payload;
    },
    onCalendarZoomIn: (state) => {
      if (!state.calendarView.schedule) return;
      const currentZoomLevel = state.calendarView.schedule
        .timeCellStep as number;
      const newIndex = Math.max(
        possibleZoomLevelsInMinutes.indexOf(
          currentZoomLevel as MobiscrollCellStepValues
        ) - 1,
        0
      );
      state.calendarView.schedule.timeCellStep =
        possibleZoomLevelsInMinutes[newIndex];
      state.calendarView.schedule.timeLabelStep =
        possibleLabelStepValuesForZoomIndex[newIndex];
    },
    onCalendarZoomOut: (state) => {
      if (!state.calendarView.schedule) return;
      const currentZoomLevel = state.calendarView.schedule
        .timeCellStep as number;
      const newIndex = Math.min(
        possibleZoomLevelsInMinutes.indexOf(
          currentZoomLevel as MobiscrollCellStepValues
        ) + 1,
        possibleZoomLevelsInMinutes.length - 1
      );
      state.calendarView.schedule.timeCellStep =
        possibleZoomLevelsInMinutes[newIndex];
      state.calendarView.schedule.timeLabelStep =
        possibleLabelStepValuesForZoomIndex[newIndex];
    },
    onCalendarZoomReset: (state) => {
      state.calendarView = {
        ...initialState.calendarView,
      };
    },
    setSelectedAssignedTripSearchId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedAssignedTripSearchId = action.payload || null;
    },
    setUnassignedTripsSorting: (
      state,
      action: PayloadAction<UnassignedTripsSorting>
    ) => {
      state.unassignedTripsSorting = action.payload;
    },
    setUnassignedTripsSortingDirection: (
      state,
      action: PayloadAction<'asc' | 'desc'>
    ) => {
      state.unassignedTripsSortingDirection = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IDispatchState>) => {
    builder.addCase(execTripMenuAction.pending, (state, action) => {
      state.isMenuActionInProgress = true;
      state.pendingTripIds = action.meta.arg.tripIds ?? [];
      state.activeMenuActionRequestId = action.meta.requestId;
    });

    builder.addCase(execTripMenuAction.fulfilled, (state, action) => {
      // A completion from a superseded action must not clear state the
      // newer in-flight action owns.
      if (action.meta.requestId !== state.activeMenuActionRequestId) {
        return;
      }
      state.isMenuActionInProgress = false;
      state.activeMenuActionRequestId = null;
      // pendingTripIds intentionally stays set: the trip keeps its dimmed
      // treatment until the refetched board reflects the action
      // (dispatchRetrieve matchers below).
    });

    builder.addCase(execTripMenuAction.rejected, (state, action) => {
      if (action.meta.requestId !== state.activeMenuActionRequestId) {
        return;
      }
      state.isMenuActionInProgress = false;
      state.activeMenuActionRequestId = null;
      // Error terminal for a pending drag action: the toast reports the
      // failure and the trip stays on its source crew.
      state.pendingTripAction = null;
      state.pendingTripIds = [];
    });

    builder.addMatcher(
      bambiApi.endpoints.dispatchRetrieve.matchFulfilled,
      (state) => {
        // The board now lives in the RTK Query cache (selectEffectiveDispatchResponse
        // reads it), so there is no mirror to sync here. This matcher is retained
        // only as the success terminal for a pending drag action: keeping it set
        // until fresh data arrives (assign/reassign invalidate the dispatch tag)
        // means the destination preview persists right up to the refetch — no gap
        // where the trip renders back at its source.
        state.previewEvents = undefined;
        state.pendingTripAction = null;
        state.pendingTripIds = [];
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.dispatchRetrieve.matchRejected,
      (state, action) => {
        // A failed refetch would otherwise leave the pending action (and its
        // preview) up forever — previews pause polling, so nothing else would
        // come along to clear it. ConditionErrors are rtk-query internals
        // (duplicate/aborted requests), not real fetch failures.
        if (action.error.name !== 'ConditionError') {
          state.pendingTripAction = null;
          state.pendingTripIds = [];
        }
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunCreate.matchPending,
      (state) => {
        state.isRunBambiRunInProgress = true;
        state.runBambiRunJobId = undefined;
        state.runBambiRunError = null;
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunCreate.matchRejected,
      (state, action) => {
        state.isRunBambiRunInProgress = false;
        state.runBambiRunError = serverErrorFormatter(action.payload);
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunCreate.matchFulfilled,
      (state, action) => {
        // We don't set loading to false because we will poll this id
        state.runBambiRunJobId = action.payload.id;
        state.isRunBambiRunInProgress = true;
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunRetrieve.matchPending,
      (state) => {
        // Not really necessary, but for completeness ensure this is true here
        state.isRunBambiRunInProgress = true;
        state.runBambiRunError = null;
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunRetrieve.matchRejected,
      (state, action) => {
        // We can/should ignore these errors from rtk-query because they occur when
        // dupe requests are made most often. It's an internal error and doesn't necessarily mean that there was
        // an http error
        if (action.error.name !== 'ConditionError') {
          state.isRunBambiRunInProgress = false;
          state.runBambiRunError = 'Error fetching Run Bambi Run task status';
        }
      }
    );

    builder.addMatcher(
      bambiApi.endpoints.runBambiRunRetrieve.matchFulfilled,
      (state, action) => {
        switch (action.payload.state) {
          case 'STARTED':
          case 'PENDING':
            return;
          case 'FAILURE':
            state.isRunBambiRunInProgress = false;
            // RBR returns an empty object on 'FAILURE' state
            // Isolating this so FE only deals with undefined or RunBambiRunResultRead
            state.runBambiRunResult = {
              new_trip_assignments: [],
              trip_reassignments: [],
              assignments: [],
              trips_not_assigned: [],
              existing_trip_assignments: [],
            };
            state.runBambiRunError =
              'Run Bambi Run failed to complete. Please try again by clicking Cancel and re-running.';
            return;
        }
        state.runBambiRunResult = action.payload.result;
        state.isRunBambiRunInProgress = false;
        // We want to select all new assignments by default
        state.runBambiRunResult.new_trip_assignments.forEach((trip) => {
          state.tripAssignmentsToApplyIds[trip.id] = true;
        });
        // Annnnnd reassignments also
        state.runBambiRunResult.trip_reassignments.forEach((trip) => {
          state.tripAssignmentsToApplyIds[trip.id] = true;
        });
      }
    );

    // After a single-trip requested-assign completes, drop it from the
    // unassigned selection. The former optimistic board move (unassigned →
    // assigned) is retired with the mirror: a matcher can't patch the RTK cache,
    // and this was an explicit "temporary fix" for *poll* latency — the trip
    // mutation now invalidates the `dispatch` tag, so the board reconciles via an
    // immediate refetch rather than waiting for the next poll. PR4's optimistic-
    // mutation layer will reinstate an instant move via updateQueryData + undo.
    builder.addMatcher(
      tripsApi.endpoints.tripsAssignPartialUpdate.matchFulfilled,
      (state, action) => {
        const tripId = action.meta.arg.originalArgs.id;
        state.selectedUnassignedTripIds =
          state.selectedUnassignedTripIds.filter((id) => id !== tripId);
      }
    );
  },
});

export const {
  setSelectedUnassignedTripIds,
  setPendingTripIds,
  setUnassignedTripsSearchTerm,
  setUnassignedTripsFilters,
  setOpenTripDropdownMenuId,
  onTripAssignmentInvalid,
  onBulkTripAssignmentInvalid,
  onRequestedTripAssignment,
  onCancelAssignTrip,
  onCancelBulkAssignTrip,
  onConfirmAssignTrip,
  onConfirmBulkAssignTrip,
  onWillCallScheduleOverride,
  onCancelWillCallScheduleOverride,
  onConfirmWillCallScheduleOverride,
  onRunBambiRunShouldMultiloadChange,
  resetSelectedTrip,
  setModalAssignmentBreak,
  setModalAssignmentId,
  setModalSelectedTrip,
  setModalCallRequestInputs,
  onPreviewTripAssignmentStart,
  onPreviewTripAssignmentEnd,
  onTripActionStart,
  onTripActionEnd,
  toggleDispatchSidebar,
  showDispatchModal,
  dismissDispatchModal,
  setShowToggleCalendarScrollButton,
  setEventCalendarScrollable,
  setModalConfirmTripActivationTripId,
  onSingleTripRequestRejection: onTripMenuRejectRequest,
  onSingleTripRequestAcceptance: onTripMenuAcceptRequest,
  onTripMenuCancelRequest,
  onRejectReasonChange,
  setRequestedTripRejectReasons,
  onBulkAssignRequestedValidTrips,
  onApplySelectedAssignments,
  onRunBambiRunButtonClick,
  setAutoScrollCalendar,
  setViewTripDetailsOnHover,
  setAssignmentSorting,
  setSelectedAssignedTripSearchId,
  setRunBambiRunMode,
  setUnassignedTripsSorting,
  setUnassignedTripsSortingDirection,
  onBulkMultiloadAssignmentStart,
  onMultiloadReassignStart,
  onMultiloadReassignValidationError,
  onMultiloadReassignNoMerge,
  onMultiloadReassignServerError,
  onMultiloadReassignClose,
} = dispatcherSlice.actions;

export const useDispatchModalToShow = () => {
  return useAppSelector((state) => state.dispatcher.modals.modalToShow);
};

export const selectUnassignedTrips = (state: RootState) =>
  selectEffectiveDispatchResponse(state)?.unassigned_trips;

export const selectSelectedUnassignedTripIds = (state: RootState) =>
  state.dispatcher.selectedUnassignedTripIds;

export const selectPendingTripIds = (state: RootState) =>
  state.dispatcher.pendingTripIds;

export const selectAllRequestedUnassignedTrips = createSelector(
  [selectUnassignedTrips],
  (unassignedTrips) =>
    unassignedTrips?.filter((trip) => trip.status === 'requested')
);

export const selectUnassignedSelectedRequestedTrips = createSelector(
  [selectSelectedUnassignedTripIds, selectUnassignedTrips],
  (selectedUnassignedTripIds, unassignedTrips) => {
    return unassignedTrips?.filter(
      (trip) =>
        trip.status === 'requested' &&
        selectedUnassignedTripIds.includes(trip.id)
    );
  }
);
